import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { NAV_LINKS } from "../../lib/mrs";

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      data-testid="main-navbar"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 1, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "backdrop-blur-xl bg-[#0a0b0a]/70 border-b border-[#eae7e0]/10"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <nav className="flex items-center justify-between px-6 md:px-12 lg:px-20 h-20">
        <a
          href="#top"
          data-testid="nav-brand"
          className="group flex items-center pl-1"
          aria-label="MRS Builders home"
        >
          <img
            src="https://customer-assets.emergentagent.com/job_contact-landing-4/artifacts/gjeohswk_2.png"
            alt="MRS Builders"
            className="h-12 md:h-14 w-auto rounded-sm transition-transform duration-500 group-hover:scale-105"
          />
        </a>

        <div className="hidden lg:flex items-center gap-10">
          {NAV_LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className="link-underline text-[13px] tracking-[0.15em] uppercase text-[#9ca399] hover:text-[#eae7e0] transition-colors duration-300"
            >
              {l.label}
            </a>
          ))}
        </div>

        <div className="hidden lg:block">
          <a
            href="#contact"
            data-testid="nav-cta-estimate"
            className="inline-flex items-center px-6 py-3 text-[12px] tracking-[0.2em] uppercase bg-[#b89b72] text-[#0a0b0a] hover:bg-[#cbb38e] transition-colors duration-300"
          >
            Get an Estimate
          </a>
        </div>

        <button
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen((v) => !v)}
          className="lg:hidden text-[#eae7e0] p-2"
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="lg:hidden overflow-hidden backdrop-blur-xl bg-[#0a0b0a]/95 border-t border-[#eae7e0]/10"
          >
            <div className="flex flex-col px-6 py-6 gap-5">
              {NAV_LINKS.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                  onClick={() => setOpen(false)}
                  className="text-lg font-display tracking-wide text-[#eae7e0]"
                >
                  {l.label}
                </a>
              ))}
              <a
                href="#contact"
                onClick={() => setOpen(false)}
                data-testid="nav-mobile-cta"
                className="mt-2 inline-flex justify-center px-6 py-3 text-[12px] tracking-[0.2em] uppercase bg-[#b89b72] text-[#0a0b0a]"
              >
                Get an Estimate
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
